import React, { useEffect } from 'react';
import {
  Accordion,
  Badge,
  Box,
  Flex,
  Group,
  Skeleton,
  Text,
} from '@mantine/core';
import { useIntersection } from '@mantine/hooks';
import { DiscoveryEvidenceDetails } from './DiscoveryEvidenceDetails';
import { DiscoveryEvidenceHeader } from './DiscoveryEvidenceHeader';
import { useEvidenceListData } from '../../../hooks/useEvidenceListData';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import type { EnrichedEvidence } from './types';
import { AssignedToIdeaTag, shouldShowAssignedTag } from '../../evidences/AssignedToIdeaTag';

function getAssignedIdeaTitle(evidence: EnrichedEvidence): string {
  const title = evidence.idea?.title?.trim();
  if (title) return title;
  return 'Idea';
}

function AssignedToIdeaBanner({
  evidence,
  canOpenIdeaLink,
  isIdeaArtifact,
}: {
  evidence: EnrichedEvidence;
  canOpenIdeaLink: boolean;
  isIdeaArtifact: boolean;
}) {
  const ideaTitle = getAssignedIdeaTitle(evidence);

  if (isIdeaArtifact) {
    return (
      <Box>
        <Flex px={10} py={6} align="center" justify="flex-end" gap={8} wrap="nowrap" sx={{ minWidth: 0, borderBottom: '1px solid #E9ECEF' }}>
          <Text fz={12} c="dimmed" sx={{ flexShrink: 0 }}>
            Validated
          </Text>
        </Flex>
      </Box>
    );
  }

  if (!canOpenIdeaLink) {
    // Users without permission see plain text rather than clickable idea links / Assign button
    return (
      <Box>
        <Flex px={10} py={6} align="center" justify="flex-end" gap={8} wrap="nowrap" sx={{ minWidth: 0, borderBottom: '1px solid #E9ECEF' }}>
          <Text fz={12} c="dimmed" sx={{ flexShrink: 0 }}>
            {ideaTitle || 'Assigned to Idea'}
          </Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      <Flex px={10} py={6} align="center" justify="flex-end" gap={8} wrap="nowrap" sx={{ minWidth: 0, borderBottom: '1px solid #E9ECEF' }}>
        <AssignedToIdeaTag evidence={evidence} compact />
      </Flex>
    </Box>
  );
}

export interface DiscoveryEvidenceListSectionProps {
  evidenceIds: string[];
  isIdeaArtifact: boolean;
}

function EvidencesSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} height={48} radius="md" mb="sm" />
      ))}
    </>
  );
}

export const DiscoveryEvidenceListSection: React.FC<DiscoveryEvidenceListSectionProps> = ({
  evidenceIds,
  isIdeaArtifact,
}) => {
  const {
    evidences,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    total,
    error,
  } = useEvidenceListData(evidenceIds);

  const { ref: sentinelRef, entry } = useIntersection({
    root: null,
    threshold: 0.1,
  });

  useEffect(() => {
    if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [entry?.isIntersecting, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (evidenceIds.length === 0) {
    return null;
  }

  if (error) {
    return (
      <Box px="md" py="sm">
        <Text size="sm" c="red">
          {error.message || 'Failed to load evidences.'}
        </Text>
      </Box>
    );
  }

  const showInitialSkeleton = isLoading;
  const showMoreSkeleton = !isLoading && isFetchingNextPage;

  return (
    <Box sx={{ minWidth: 0, maxWidth: '100%', width: '100%', overflow: 'hidden' }}>
      <Group px="md" align="center" position='apart' mb="md">
        <Text fw={500} c="#212529" sx={{ fontSize: 16, lineHeight: 1.3, maxWidth: '85%' }}>
          Evidences
        </Text>
        <Badge styles={{
          root: {
            backgroundColor: '#fff',
            borderColor: '#E9ECEF',
          },
        }} radius="xs" p={4}>
          {total}
        </Badge>
      </Group>

      {showInitialSkeleton ? (
        <Box px="md">
          <EvidencesSkeleton rows={3} />
        </Box>
      ) : evidences.length === 0 ? (
        <Text px="md" size="sm" c="dimmed">
          No evidences found for this insight.
        </Text>
      ) : (
        <Box px="md" pb="md">
          <Accordion
            variant="separated"
            radius="md"
            multiple
            defaultValue={evidences.length === 1 ? [String(evidences[0]._id)] : undefined}
            styles={(theme) => ({
              item: {
                border: '1px solid #E9ECEF',
                backgroundColor: '#fff',
                marginBottom: 8,
                marginTop: 0,
                borderRadius: theme.radius.md,
                overflow: 'hidden',
                transition: 'border-color 150ms ease',
                '&:hover': {
                  borderColor: '#5C5CEB',
                },
              },
              control: {
                padding: '6px 10px',
                transition: 'background-color 150ms ease',
                '&:hover': {
                  backgroundColor: '#F8F9FA',
                },
                '&[data-active]': {
                  borderBottom: '1px solid #E9ECEF',
                },
              },
              panel: {
                paddingTop: 8,
                paddingBottom: 10,
                paddingInline: 10,
              },
            })}
          >
            {evidences.map((e) => (
              <EvidenceAccordionItem key={String(e._id)} evidence={e} isIdeaArtifact={isIdeaArtifact} />
            ))}
          </Accordion>
          <Box ref={sentinelRef} h={1} />
          {showMoreSkeleton && (
            <Box mt="sm">
              <EvidencesSkeleton rows={3} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

function EvidenceAccordionItem({ evidence, isIdeaArtifact }: { evidence: EnrichedEvidence, isIdeaArtifact: boolean }) {
  const id = String(evidence._id);
  const assigned = shouldShowAssignedTag(evidence);
  const { canOpenEvidencePopup } = useUserPermissions();

  return (
    <Accordion.Item value={id}>
      {assigned && (
        <AssignedToIdeaBanner
          evidence={evidence}
          canOpenIdeaLink={canOpenEvidencePopup}
          isIdeaArtifact={isIdeaArtifact}
        />
      )}
      <Accordion.Control py={0}>
        <DiscoveryEvidenceHeader evidence={evidence} />
      </Accordion.Control>
      <Accordion.Panel>
        <DiscoveryEvidenceDetails
          evidence={evidence}
          variant="full"
          showMetadata
          panelPreset="exploreAccordion"
        />
      </Accordion.Panel>
    </Accordion.Item>
  );
}

export default DiscoveryEvidenceListSection;
